import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useInitialDataOfSingleMedicalOrder } from "../context/initialDataOfSingleMedicalOrder";
import { JCodeDetails } from "../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../enums/eventsData";
import { EventsName } from "../enums/eventsName";
import { RootState } from "../redux/store/store";
import { getAuthStatusOfOneJCode } from "../utils/getAuthStatusOfMedicalPaOrder";
import { getOrgIdForFetchExternalWorklist } from "../utils/organizationHelper";
import { apiWorkerManager } from "../workerManagers/workerManager";
/**
 * Logs J-Code status events to BigQuery.
 *
 * @async
 * @function logJCodeStatusEvents
 * @param {JCodeDetails[]} jCodes
 *   Array of JCodes to evaluate. Non-array or empty input is ignored.
 *
 * @param {number} delay
 *   Delay (ms) passed to `apiWorkerManager.processPayloads`, which controls background processing.
 *
 * @description
 * For each JCode:
 *  - Builds an event payload containing order/user/coverage metadata
 */

export const useLogJCodeStatusEvents = () => {
  const { singleMedicalPaOrder } = useSelector(
    (s: RootState) => s.singleMedicalPaOrder,
  );
  const { data: authStatusOptions } = useSelector(
    (s: RootState) => s.authStatusOptions,
  );
  const { user } = useSelector((s: RootState) => s.firebaseAuthentication);
  const { oldPrimaryIcdCodes, countOfPrimaryIcdCodes } =
    useInitialDataOfSingleMedicalOrder();

  const newPrimaryIcdCodes = useMemo(() => {
    const details = singleMedicalPaOrder?.primary_diagnosis_details;
    if (Array.isArray(details)) {
      return details.map((detail) => detail.icd_code ?? "");
    }
    return [];
  }, [singleMedicalPaOrder]);

  const logJCodeStatusEvents = async (
    jCodes: JCodeDetails[],
    delay: number,
  ) => {
    if (!Array.isArray(jCodes) || jCodes.length === 0) return;
    if (!authStatusOptions) return;

    const patient_id = singleMedicalPaOrder?.patient_id ?? "";
    const order_id = singleMedicalPaOrder?.order_id ?? "";
    const org_id = getOrgIdForFetchExternalWorklist() ?? "";
    const user_id = user?.id ?? "";
    const user_email = user?.email ?? "";
    const payer_name =
      singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "";
    const plan_name = singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "";
    const itemsToStore = jCodes
      .map((jCode) => {
        const authStatus = getAuthStatusOfOneJCode(jCode, authStatusOptions);

        return {
          patient_id,
          order_id,
          org_id,
          event_name: EventsName.J_CODE_STATUS,
          user_id,
          user_email,
          additional_data: {
            screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
            payer_name,
            plan_name,
            j_code: jCode.j_code,
            auth_status: authStatus?.text ?? "",
            icd_state: jCode.nccn_match ?? "",
            old_primary_icd_codes: (oldPrimaryIcdCodes ?? []).join(","),
            count_of_icd_codes: countOfPrimaryIcdCodes ?? 0,
            new_primary_icd_codes: newPrimaryIcdCodes.join(","),
            icd_codes_changed:
              (newPrimaryIcdCodes ?? []).join(",") !==
              (oldPrimaryIcdCodes ?? []).join(","),
          },
        };
      })
      .filter(Boolean);

    if (itemsToStore.length === 0) return;

    const batchKey = `JCODE_LOG_QUEUE_${order_id}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    apiWorkerManager.enqueueAndProcess(itemsToStore, batchKey, delay);
  };

  return logJCodeStatusEvents;
};
